import 'core-js/actual/string/is-well-formed';
import 'core-js/actual/string/to-well-formed';

/**
 * Replaces unpaired UTF-16 surrogates without changing valid surrogate pairs.
 *
 * JavaScript strings can contain lone surrogate code units even though they
 * cannot be encoded as valid UTF-8. Model providers reject those strings, so
 * prompt and model-output boundaries must repair them before serialization.
 */
export function makeUnicodeWellFormed(value: string): string {
  return value.toWellFormed();
}

/**
 * Repairs malformed Unicode and truncates to a UTF-16 code-unit limit without
 * retaining only the high surrogate from a valid non-BMP character.
 */
export function truncateUtf16(value: string, maxLength: number): string {
  const safeMaxLength = Math.max(0, maxLength);
  let truncated = makeUnicodeWellFormed(value).slice(0, safeMaxLength);
  if (/[\uD800-\uDBFF]$/.test(truncated)) {
    truncated = truncated.slice(0, -1);
  }
  return truncated;
}

/**
 * Trims, then truncates text that exceeds `limit` to a word boundary with a
 * trailing ellipsis. Text within the limit is returned trimmed but uncut.
 *
 * Used to bound model-written free text (blurbs, reasons) where prompts ask
 * for a length that models routinely overshoot — schema-level hard caps on
 * such fields fail the whole generation instead.
 */
export function truncateTextWithEllipsis(value: string, limit: number): string {
  // Repair malformed Unicode on the fast path too, so within-limit text keeps
  // the same well-formedness guarantee as the truncateUtf16 path below.
  const trimmed = makeUnicodeWellFormed(value).trim();
  if (trimmed.length <= limit) {
    return trimmed;
  }

  // Code-point-safe cut: a plain .slice() could strand the high surrogate of
  // an astral character (e.g. an emoji) sitting exactly at the boundary.
  const slice = truncateUtf16(trimmed, limit - 1);
  const lastWordBreak = slice.lastIndexOf(' ');
  const truncated = lastWordBreak > 0 ? slice.slice(0, lastWordBreak) : slice;
  return `${truncated.trimEnd()}…`;
}
